import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RefreshCw, Sparkles, Loader2, AlertCircle, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ProductHuntPreview } from "@/components/launch/ProductHuntPreview";
import { ProductHuntEditor } from "@/components/launch/ProductHuntEditor";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// ProductHunt Logo Component
const ProductHuntLogo = () => (
  <div className="rounded-full bg-orange-500 h-10 w-10 flex items-center justify-center">
    <span className="text-white font-bold text-xl">P</span>
  </div>
);

const ProductHuntGuidePage = () => {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState("");
  const [generationError, setGenerationError] = useState<string | null>(null);
  
  const [content, setContent] = useState<{
    id: string;
    taglines: string[];
    short_descriptions: string[];
    full_descriptions: string[];
  } | null>(null);
  
  const [initialLoad, setInitialLoad] = useState(true);
  const [activeTab, setActiveTab] = useState("preview");
  
  // Selected content for preview
  const [selectedTaglineIndex, setSelectedTaglineIndex] = useState(0);
  const [selectedShortDescIndex, setSelectedShortDescIndex] = useState(0);
  const [selectedFullDescIndex, setSelectedFullDescIndex] = useState(0);
  const [userName, setUserName] = useState("Your Name");
  
  // Project name
  const [projectName, setProjectName] = useState("Your Product");

  const fetchProjectDetails = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("projects")
        .select("name")
        .eq("id", projectId)
        .single();

      if (error) {
        console.error("Error fetching project details:", error);
      } else if (data) {
        setProjectName(data.name);
      }
    } catch (error) {
      console.error("Error fetching project details:", error);
    }
  }, [projectId]);

  const fetchExistingContent = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("producthunt_content")
        .select("*")
        .eq("project_id", projectId)
        .eq("user_id", user?.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching content:", error);
        toast({
          title: "Error",
          description: "Failed to load existing content",
          variant: "destructive",
        });
      } else if (data) {
        // Ensure we have proper data structure
        const processedData = {
          id: data.id,
          // Cast JSON arrays to string arrays
          taglines: Array.isArray(data.taglines) ? data.taglines.map(String) : [],
          short_descriptions: Array.isArray(data.short_descriptions) ? data.short_descriptions.map(String) : [],
          // Handle single string full_description and convert to array
          full_descriptions: data.full_description ? [data.full_description] : []
        };
        setContent(processedData);
      }
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      setInitialLoad(false);
    }
  }, [projectId, user?.id, toast]);

  // Fetch project details and existing content on load
  useEffect(() => {
    if (projectId && user) {
      fetchProjectDetails();
      fetchExistingContent();
    }
  }, [projectId, user, fetchProjectDetails, fetchExistingContent]);

  // Handle selection changes from editor
  const handleSelectionChange = (type: string, index: number) => {
    if (type === 'tagline') {
      setSelectedTaglineIndex(index);
    } else if (type === 'shortDesc') {
      setSelectedShortDescIndex(index);
    } else if (type === 'fullDesc') {
      setSelectedFullDescIndex(index);
    }
  };

  // Handle user name change
  const handleUserNameChange = (name: string) => {
    setUserName(name);
  };

  // Simulated progress steps for the loader
  const generationSteps = [
    { progress: 10, task: "Analyzing website data and extracting key information..." },
    { progress: 25, task: "Identifying unique value propositions and target audience..." },
    { progress: 40, task: "Crafting compelling taglines with viral potential..." },
    { progress: 60, task: "Generating concise product descriptions for maximum impact..." },
    { progress: 75, task: "Creating comprehensive Product Hunt launch content..." },
    { progress: 90, task: "Optimizing content for engagement and conversion..." },
    { progress: 100, task: "Finalizing and preparing your Product Hunt content..." }
  ];

  const generateContent = async () => {
    if (!projectId || !user) {
      toast({
        title: "Error",
        description: "Missing project or user information",
        variant: "destructive",
      });
      return;
    }
    
    setIsGenerating(true);
    setGenerationError(null);
    setProgress(0);
    setCurrentTask(generationSteps[0].task);
    
    // Start progress simulation
    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      if (stepIndex < generationSteps.length - 1) {
        stepIndex++;
        setProgress(generationSteps[stepIndex].progress);
        setCurrentTask(generationSteps[stepIndex].task);
      } else {
        clearInterval(progressInterval);
      }
    }, 3000);
    
    try {
      console.log("Calling generate-producthunt-content function");
      
      const { data, error } = await supabase.functions.invoke('generate-producthunt-content', {
        body: { 
          projectId,
          userId: user.id
        }
      });
      
      clearInterval(progressInterval);
      
      if (error) {
        throw new Error(error.message || "Failed to generate content");
      }
      
      if (!data || !data.id) {
        throw new Error("Invalid response format from server");
      }
      
      // Process the response data
      const processedData = {
        id: data.id,
        // Cast JSON arrays to string arrays
        taglines: Array.isArray(data.taglines) ? data.taglines.map(String) : [],
        short_descriptions: Array.isArray(data.short_descriptions) ? data.short_descriptions.map(String) : [],
        // Handle full_description, ensuring we convert to a string array
        full_descriptions: data.full_description 
          ? [String(data.full_description)] 
          : []
      };
      
      setProgress(100);
      setCurrentTask("Content successfully generated!");
      
      // Short delay to show the completion message
      setTimeout(() => {
        setContent(processedData);
        setIsGenerating(false);
      }, 1000);
      
      toast({
        title: "Content Generated",
        description: "Your Product Hunt content has been successfully generated",
      });
    } catch (error) {
      clearInterval(progressInterval);
      console.error("Error generating content:", error);
      setGenerationError(error.message || "Failed to generate content");
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate content",
        variant: "destructive",
      });
      setIsGenerating(false);
    }
  };

  const handleContentChange = (type: string, index: number, value: string) => {
    if (!content) return;
    
    const updatedContent = { ...content };
    
    switch (type) {
      case 'tagline':
        updatedContent.taglines[index] = value;
        break;
      case 'shortDesc':
        updatedContent.short_descriptions[index] = value;
        break;
      case 'fullDesc':
        updatedContent.full_descriptions[index] = value;
        break;
      default:
        break;
    }
    
    setContent(updatedContent);
  };

  // Loading state during initial fetch
  if (initialLoad) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-zinc-200 dark:border-zinc-800">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-orange-500/10">
                <ProductHuntLogo />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">
                  Product Hunt Generator
                </h1>
                <p className="text-zinc-500 dark:text-zinc-400">
                  Create compelling content for your Product Hunt launch
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 p-4">
              <Skeleton className="h-12 w-64 mb-6" />
              <div className="grid gap-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-48 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Custom AI Team Loading component with tech-focused text
  const TechAITeamLoading = () => (
    <div className="space-y-8 py-6">
      <div className="text-center space-y-3">
        <div className="inline-block p-4 rounded-full bg-orange-100 dark:bg-orange-900/30 mb-2">
          <ProductHuntLogo />
        </div>
        <h2 className="text-2xl font-bold text-orange-700 dark:text-orange-400">AI Analysis in Progress</h2>
        <p className="text-zinc-500 max-w-md mx-auto">
          Our advanced AI agents are processing your website data to create optimized Product Hunt content
        </p>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="font-medium text-orange-700 dark:text-orange-400">Analysis Progress</span>
          <span className="font-medium">{progress}%</span>
        </div>
        <div className="h-2 bg-orange-100 dark:bg-orange-900/30 rounded-full overflow-hidden">
          <div 
            className="h-full bg-orange-500 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="text-sm text-zinc-500 mt-2">{currentTask}</p>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto p-6">
      <Card className="border-zinc-200 dark:border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-orange-500/10">
                <ProductHuntLogo />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">
                  Product Hunt Generator
                </h1>
                <p className="text-zinc-500 dark:text-zinc-400">
                  Create compelling content for your Product Hunt launch
                </p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isGenerating ? (
            <TechAITeamLoading />
          ) : content ? (
            <div className="space-y-8 animate-in fade-in">
              <Alert className="bg-orange-50 border-orange-200 dark:bg-orange-950/10 dark:border-orange-900/50">
                <Info className="h-4 w-4 text-orange-500" />
                <AlertTitle className="text-orange-700 dark:text-orange-400">Product Hunt Launch Preview</AlertTitle>
                <AlertDescription className="text-orange-600/80 dark:text-orange-400/80">
                  Below is a preview of how your product might look on Product Hunt. Select content options from the editor tab.
                </AlertDescription>
              </Alert>
              
              <Tabs 
                defaultValue="preview" 
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-6">
                  <TabsTrigger 
                    value="preview" 
                    className="data-[state=active]:bg-orange-500 data-[state=active]:text-white"
                  >
                    Preview
                  </TabsTrigger>
                  <TabsTrigger 
                    value="editor"
                    className="data-[state=active]:bg-zinc-100 data-[state=active]:text-zinc-900 dark:data-[state=active]:bg-zinc-800 dark:data-[state=active]:text-zinc-100"
                  >
                    Content Editor
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="preview" className="mt-0">
                  <div className="space-y-6">
                    <ProductHuntPreview 
                      productName={projectName}
                      tagline={content.taglines[selectedTaglineIndex] || "Your catchy tagline goes here"}
                      shortDescription={content.short_descriptions[selectedShortDescIndex] || "Brief description of what your product does and who it's for"}
                      fullDescription={content.full_descriptions[selectedFullDescIndex] || ""}
                      userName={userName}
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="editor" className="mt-0">
                  <ProductHuntEditor 
                    content={content}
                    onContentChange={handleContentChange}
                    onSelectionChange={handleSelectionChange}
                    userName={userName}
                    onUserNameChange={handleUserNameChange}
                  />
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <div className="bg-orange-500/10 p-4 rounded-full mb-6">
                <ProductHuntLogo />
              </div>
              <h2 className="text-2xl font-semibold mb-2">Ready to Create Your Product Hunt Content</h2>
              <p className="text-zinc-500 max-w-md mb-8">
                Generate compelling taglines, descriptions, and a full Product Hunt page description based on your website analysis.
              </p>
              
              {generationError && (
                <Alert variant="destructive" className="mb-6 max-w-xl">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Generation Failed</AlertTitle>
                  <AlertDescription>{generationError}</AlertDescription>
                </Alert>
              )}
              
              <Button 
                onClick={generateContent}
                className="gap-2 bg-orange-500 hover:bg-orange-600 text-white"
                size="lg"
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate Content
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductHuntGuidePage;
